import { Controller } from '@hotwired/stimulus'
import analytics from '../lib/analytics'

// Lightweight analytics Stimulus controller.
// Usage:
// - Add to CTA elements: data-controller="analytics" data-action="click->analytics#track" data-analytics-event="cta_click" data-analytics-label="Label"
// - Add to hero element (optional) to auto-send hero_view when visible: data-controller="analytics" data-analytics="hero"
// The controller will attempt to send events to Mixpanel (mixpanel.track) or GA4 (gtag), and fall back to console.log.

export default class extends Controller {
  connect() {
    try {
      // Ensure global listeners added only once
      const win = window as any
      if (!win.__pacta_analytics_initialized) {
        // Listen for FAQ open events emitted by accordion controller
        window.addEventListener('faq_open', (e: Event) => {
          const detail = (e as CustomEvent).detail || {}
          // Normalize and send useful props (index, headerId, questionId)
          try {
            const props: Record<string, unknown> = {
              index: typeof detail.index !== 'undefined' ? detail.index : null,
              id: detail.headerId || null
            }
            analytics.track('faq_open', props)
          } catch (e) { /** swallow */ }
        })

        win.__pacta_analytics_initialized = true
      }

      // If this controller is attached to an element with data-analytics, observe visibility and fire a _view event once
      const analyticsAttr = this.element.getAttribute('data-analytics')
      if (analyticsAttr) {
        const configuredEvent = (this.element as HTMLElement).dataset.analyticsEvent
        const eventName = configuredEvent || (analyticsAttr === 'hero' ? 'hero_view' : `${analyticsAttr}_view`)
        const elDataset = (this.element as HTMLElement).dataset || {}

        const observer = new IntersectionObserver((entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              try {
                const props: Record<string, unknown> = {
                  id: this.element.id || null,
                  label: elDataset.analyticsLabel || null,
                  section: elDataset.analyticsSection || null
                }
                analytics.track(eventName, props)
              } catch (e) { /** swallow */ }
              obs.disconnect()
            }
          })
        }, { threshold: 0.25 })

        observer.observe(this.element)
      }
    } catch (err) {
      // Never block page for analytics errors
      // eslint-disable-next-line no-console
      console.error('analytics controller init error', err)
    }
  }

  // Generic click handler. Wire with: data-action="click->analytics#track"
  track(event: Event) {
    try {
      const el = (event.currentTarget || event.target) as HTMLElement
      const eventName = el?.dataset?.analyticsEvent || el?.dataset?.event || 'cta_click'
      const label = el?.dataset?.analyticsLabel || el?.textContent?.trim() || null
      const href = (el as HTMLAnchorElement).href || el.getAttribute('href') || null

      const props: Record<string, unknown> = { label, href, id: el.id || null }
      // Attach any dataset entries starting with analytics (e.g. data-analytics-id="x")
      Object.keys(el.dataset || {}).forEach((k) => {
        if (!k.startsWith('analytics')) return
        // skip already-handled keys
        if (k === 'analyticsEvent' || k === 'analyticsLabel' || k === 'analytics') return
        // normalize key: analyticsSomething -> something (camelCase)
        const keyName = k.replace(/^analytics/, '')
        if (!keyName) return
        const propName = keyName.charAt(0).toLowerCase() + keyName.slice(1)
        // @ts-ignore dataset index
        props[propName] = (el.dataset as any)[k]
      })

      // Do not block rendering — send async
      Promise.resolve().then(() => analytics.track(eventName, props))
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('analytics track error', err)
    }
  }
}
